// ProfileAICreations — Tum AI uretimler: image, video, music, lipsync, avatar
// Filter + Sort + Card hover 3D tilt + Buzz link
import { ExternalLink, Filter, Image, Mic, Music, Play, SortDesc, Video } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

export interface AICreationItem {
  id: string | number
  type: 'image' | 'video' | 'music' | 'lipsync' | 'avatar'
  title: string
  thumbnail_url?: string
  url: string
  ai_tool: string // RunImage, RunVideo, RunMusicCreate vb.
  likes?: number
  created_at?: string
  buzz_post_id?: string // Buzz'da paylasildiysa
}

interface Props {
  items: AICreationItem[]
  loading?: boolean
  username?: string
}

type FilterType = 'all' | 'image' | 'video' | 'music' | 'lipsync' | 'avatar'
type SortType = 'latest' | 'most-liked' | 'trending'

const FILTER_OPTIONS: { key: FilterType; label: string; icon: React.ReactNode }[] = [
  { key: 'all', label: 'All', icon: <Filter size={12} /> },
  { key: 'image', label: 'Image', icon: <Image size={12} /> },
  { key: 'video', label: 'Video', icon: <Video size={12} /> },
  { key: 'music', label: 'Music', icon: <Music size={12} /> },
  { key: 'lipsync', label: 'LipSync', icon: <Mic size={12} /> },
]

const TOOL_COLORS: Record<string, string> = {
  RunImage: '#667eea',
  RunVideo: '#8b5cf6',
  RunMusicCreate: '#ec4899',
  RunLipSync: '#f59e0b',
  RunAvatar: '#10b981',
}

export default function ProfileAICreations({ items, loading, username }: Props) {
  const [filter, setFilter] = useState<FilterType>('all')
  const [sort, setSort] = useState<SortType>('latest')
  const observerRef = useRef<IntersectionObserver | null>(null)

  const filtered = items
    .filter(i => filter === 'all' || i.type === filter)
    .sort((a, b) => {
      if (sort === 'most-liked') return (b.likes ?? 0) - (a.likes ?? 0)
      if (sort === 'trending') return (b.likes ?? 0) - (a.likes ?? 0)
      // latest
      return new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime()
    })

  // Intersection observer — fade-in
  const cardRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        entries => entries.forEach(e => {
          if (e.isIntersecting) {
            ;(e.target as HTMLElement).style.opacity = '1'
            ;(e.target as HTMLElement).style.transform = 'translateY(0) rotateX(0)'
          }
        }),
        { threshold: 0.1 }
      )
    }
    observerRef.current.observe(node)
  }, [])

  useEffect(() => {
    return () => { observerRef.current?.disconnect() }
  }, [])

  // 3D tilt handler
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const rect = el.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width - 0.5
    const y = (e.clientY - rect.top) / rect.height - 0.5
    el.style.transform = `perspective(600px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) scale(1.02)`
    el.style.boxShadow = `${-x * 12}px ${y * 12}px 32px rgba(102,126,234,0.15)`
  }

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    el.style.transform = 'perspective(600px) rotateY(0) rotateX(0) scale(1)'
    el.style.boxShadow = 'none'
  }

  if (loading) {
    // Skeleton loader
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} style={{
            height: 200, borderRadius: 14, background: 'rgba(255,255,255,0.03)',
            border: '1px solid var(--border-default, #30363d)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }} />
        ))}
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-secondary, #8b949e)' }}>
        <Image size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
        <p style={{ fontSize: 14 }}>No AI creations yet</p>
      </div>
    )
  }

  return (
    <div>
      {/* Filter + Sort bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {FILTER_OPTIONS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 600,
                cursor: 'pointer',
                background: filter === f.key ? 'rgba(102,126,234,0.15)' : 'transparent',
                color: filter === f.key ? '#667eea' : 'var(--text-secondary, #8b949e)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {f.icon} {f.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <SortDesc size={13} color="var(--text-secondary, #8b949e)" />
          <select
            value={sort}
            onChange={e => setSort(e.target.value as SortType)}
            style={{
              background: 'transparent', border: '1px solid var(--border-default, #30363d)',
              borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 600,
              color: 'var(--text-secondary, #8b949e)', cursor: 'pointer',
            }}
          >
            <option value="latest">Latest</option>
            <option value="most-liked">Most Liked</option>
            <option value="trending">Trending</option>
          </select>
        </div>
      </div>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
        {filtered.map((item, idx) => {
          const toolColor = TOOL_COLORS[item.ai_tool] ?? '#667eea'
          return (
            <div
              key={item.id}
              ref={cardRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              style={{
                borderRadius: 14, overflow: 'hidden', position: 'relative',
                border: '1px solid var(--border-default, #30363d)',
                background: 'rgba(255,255,255,0.02)',
                opacity: 0, transform: 'translateY(20px) rotateX(2deg)',
                transition: `opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${idx * 0.04}s, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)`,
                willChange: 'transform',
              }}
            >
              {/* Thumbnail */}
              <div style={{ aspectRatio: item.type === 'video' || item.type === 'lipsync' ? '16/9' : '1/1', background: '#0d1117', position: 'relative' }}>
                {item.thumbnail_url ? (
                  <img src={item.thumbnail_url} alt={item.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1c2128, #30363d)' }}>
                    {item.type === 'video' || item.type === 'lipsync' ? <Play size={24} color={toolColor} style={{ opacity: 0.5 }} /> : <Image size={24} color={toolColor} style={{ opacity: 0.5 }} />}
                  </div>
                )}
                {/* AI tool badge */}
                <span style={{
                  position: 'absolute', top: 8, left: 8, padding: '3px 8px',
                  borderRadius: 6, fontSize: 10, fontWeight: 700,
                  background: `${toolColor}22`, color: toolColor,
                  backdropFilter: 'blur(4px)',
                  border: `1px solid ${toolColor}33`,
                }}>
                  {item.ai_tool}
                </span>
              </div>

              {/* Info */}
              <div style={{ padding: '10px 12px' }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #e6edf3)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.title}
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                  {item.created_at && (
                    <span style={{ fontSize: 11, color: 'var(--text-secondary, #8b949e)' }}>
                      {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                  {item.buzz_post_id && (
                    <a
                      href={`/buzz/post/${item.buzz_post_id}`}
                      onClick={e => e.stopPropagation()}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 3,
                        fontSize: 11, color: '#667eea', textDecoration: 'none', fontWeight: 600,
                      }}
                    >
                      <ExternalLink size={10} /> Buzz
                    </a>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
